import clsx from 'clsx';
import { ArrowLeft, Crosshair, List, LoaderCircle, Search } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FLOW } from '../constants';
import { useStore } from '../store';
import type { EpisodeEntryIntent } from '../types';

export function EpisodeSpecify() {
  const {
    episodeSpecifyId,
    episodeSpecifyStatus,
    episodeSpecifyMeta,
    episodeSpecifyBusy,
    recentEpisodeIds,
    isAdmin,
    isAuditor,
    episodeEntryIntent,
    lookupEpisode,
    showDashboard,
    showEpisodesManagement,
  } = useStore();
  const [inputValue, setInputValue] = useState(episodeSpecifyId || '');
  const [claimIntent, setClaimIntent] = useState<EpisodeEntryIntent>(episodeEntryIntent);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const blurTimerRef = useRef<number | null>(null);

  // Sync local input with store if store changes externally (e.g. via URL capture)
  useEffect(() => {
    if (episodeSpecifyId !== null) {
      setInputValue(episodeSpecifyId);
    }
  }, [episodeSpecifyId]);

  useEffect(() => {
    setClaimIntent(episodeEntryIntent);
  }, [episodeEntryIntent]);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) {
        window.clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
    };
  }, []);

  const filteredRecent = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    if (!recentEpisodeIds.length) return [];
    if (!query) return recentEpisodeIds;
    return recentEpisodeIds.filter((id) => id.toLowerCase().includes(query));
  }, [inputValue, recentEpisodeIds]);

  const handleFocus = () => {
    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setShowSuggestions(true);
  };

  const handleBlur = () => {
    blurTimerRef.current = window.setTimeout(() => {
      setShowSuggestions(false);
    }, 100);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void lookupEpisode(inputValue, { autoOpen: true, intent: claimIntent });
  };

  const handleBack = () => {
    void showDashboard();
  };

  const handleQueueClick = () => {
    void showEpisodesManagement();
  };

  const statusTone = episodeSpecifyStatus?.tone || 'info';
  const statusMessage = episodeSpecifyStatus?.message || '';
  const metaVisible = episodeSpecifyMeta?.visible || false;
  const canChooseAudit = FLOW === 'secshot' && (isAuditor || isAdmin);
  const isAuditClaim = claimIntent === 'audit-claim';

  return (
    <div className="mx-auto max-w-2xl space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col gap-2 border-b border-brand-border pb-6">
        <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-3">
          <Crosshair className="h-8 w-8 text-brand-orange" />
          {episodeEntryIntent === 'admin-readonly'
            ? 'View Episode'
            : isAuditClaim
              ? 'Claim Audit'
              : 'Claim Episode'}
        </h1>
        <p className="text-brand-text-secondary">
          {episodeEntryIntent === 'admin-readonly'
            ? 'Open any existing episode in read-only mode.'
            : isAuditClaim
              ? 'Enter an episode ID to claim it for audit.'
              : 'Enter an episode ID to claim it for annotation.'}
        </p>
      </div>

      <div className="rounded-xl border border-brand-border bg-brand-surface p-6 shadow-sm">
        {episodeEntryIntent !== 'admin-readonly' && canChooseAudit && (
          <div className="mb-5 inline-flex rounded-lg border border-brand-border bg-brand-dark p-1">
            <button
              type="button"
              aria-pressed={!isAuditClaim}
              onClick={() => setClaimIntent('operator-claim')}
              className={clsx(
                'h-8 min-w-24 rounded-md px-3 text-xs font-semibold transition-colors',
                !isAuditClaim
                  ? 'bg-brand-surface-highlight text-white'
                  : 'text-brand-text-secondary hover:text-white'
              )}
            >
              Annotate
            </button>
            <button
              type="button"
              aria-pressed={isAuditClaim}
              onClick={() => setClaimIntent('audit-claim')}
              className={clsx(
                'h-8 min-w-24 rounded-md px-3 text-xs font-semibold transition-colors',
                isAuditClaim
                  ? 'bg-brand-orange text-white'
                  : 'text-brand-text-secondary hover:text-white'
              )}
            >
              Audit
            </button>
          </div>
        )}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-2">
            <label
              htmlFor="episodeId"
              className="text-xs font-medium uppercase tracking-wider text-brand-text-secondary"
            >
              Episode ID
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-text-secondary" />
              <input
                id="episodeId"
                type="text"
                placeholder="e.g. 20231027_123456"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabled={episodeSpecifyBusy}
                className="w-full rounded-lg border border-brand-border bg-brand-dark pl-10 pr-4 py-2.5 text-sm text-brand-text-primary placeholder:text-brand-text-secondary/50 focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange transition-all"
              />
            </div>
            {showSuggestions && filteredRecent.length > 0 && (
              <div className="mt-2 rounded-lg border border-brand-border bg-brand-dark/70 p-2 text-xs text-brand-text-secondary">
                <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-brand-text-secondary">
                  Recent
                </div>
                <div className="max-h-40 overflow-y-auto">
                  {filteredRecent.map((id) => (
                    <button
                      key={id}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setInputValue(id);
                      }}
                      className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs text-brand-text-primary hover:bg-brand-surface"
                    >
                      <span className="font-mono">{id}</span>
                      <span className="text-[10px] text-brand-text-secondary">Use</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={episodeSpecifyBusy || !inputValue.trim()}
            className="inline-flex h-[42px] items-center justify-center rounded-lg bg-brand-orange px-6 text-sm font-semibold text-white shadow-md transition-all hover:bg-brand-orange-hover hover:shadow-lg disabled:opacity-50 sm:w-auto"
          >
            {episodeSpecifyBusy && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
            {episodeSpecifyBusy
              ? 'Preparing...'
              : episodeEntryIntent === 'admin-readonly'
                ? 'View Episode'
                : isAuditClaim
                  ? 'Claim Audit'
                  : 'Claim Episode'}
          </button>
        </form>

        {episodeSpecifyBusy && (
          <div
            className="mt-4 h-1.5 overflow-hidden rounded bg-brand-border"
            role="progressbar"
            aria-label="Preparing episode"
          >
            <div className="h-full w-1/3 animate-pulse rounded bg-brand-orange" />
          </div>
        )}

        {statusMessage && (
          <div
            className={clsx(
              'mt-6 rounded-lg border px-4 py-3 text-sm font-medium animate-in fade-in slide-in-from-top-2',
              statusTone === 'error' && 'bg-red-500/10 text-red-200 border-red-500/20',
              statusTone === 'warning' && 'bg-amber-500/10 text-amber-200 border-amber-500/20',
              statusTone === 'success' &&
                'bg-emerald-500/10 text-emerald-200 border-emerald-500/20',
              statusTone === 'info' &&
                'bg-brand-surface-highlight text-brand-text-primary border-brand-border'
            )}
          >
            {statusMessage}
          </div>
        )}
      </div>

      {metaVisible && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-text-secondary mb-2">
              Queue Status
            </div>
            <div className="text-lg font-mono text-brand-text-primary">
              {episodeSpecifyMeta?.queue}
            </div>
          </div>
          <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-text-secondary mb-2">
              My History
            </div>
            <div className="text-lg font-mono text-brand-text-primary">
              {episodeSpecifyMeta?.history}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-4 pt-4 border-t border-brand-border">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-2 text-sm font-medium text-brand-text-secondary hover:text-brand-orange transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>
        {isAdmin && (
          <button
            type="button"
            onClick={handleQueueClick}
            className="flex items-center gap-2 text-sm font-medium text-brand-text-secondary hover:text-white transition-colors ml-auto"
          >
            <List className="h-4 w-4" /> View Full Queue
          </button>
        )}
      </div>
    </div>
  );
}
